import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    Users,
    ChevronDown,
    ChevronUp,
    Building2,
    AlertTriangle,
    RefreshCw,
    Search,
    Filter,
    Download,
    ShieldAlert,
    CheckCircle,
    ChevronLeft,
    ChevronRight,
    Plus,
    Target,
    Edit,
    Trash2,
} from 'lucide-react';
import {
    apiClient,
    SiteRiskLeaderboardEntry,
    CreateSiteRequest,
    UpdateSiteRequest,
    Site,
    BreakdownCategoryDefinition,
} from '../services/api';
import { iconComponents, severityColorStrings as severityColors } from '../utils/ui-mappings';
import { useScoringConfig } from '../contexts/ScoringConfigContext';
import { useSort } from '../hooks/useSort';
import AddSiteModal from './Modals/AddSiteModal';
import EditSiteModal from './Modals/EditSiteModal';
import DeleteSiteModal from './Modals/DeleteSiteModal';

const ITEMS_PER_PAGE = 15;

/**
 * Site Management Page
 * Combined site management + risk scoring dashboard
 * Supports pagination, filtering, search, site creation, and detailed breakdowns
 */
const SitesPage: React.FC = () => {
    const navigate = useNavigate();
    const { lowMediumBoundary, mediumHighBoundary } = useScoringConfig();
    const [leaderboard, setLeaderboard] = useState<SiteRiskLeaderboardEntry[]>([]);
    const [breakdownCategories, setBreakdownCategories] = useState<BreakdownCategoryDefinition[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Add Site Modal State
    const [showAddSite, setShowAddSite] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    // Edit Site Modal State
    const [showEditSite, setShowEditSite] = useState(false);
    const [selectedSite, setSelectedSite] = useState<Site | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    // Delete Site State
    const [siteToDelete, setSiteToDelete] = useState<{ id: string; name: string; totalDrivers: number } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Filtering & Search State
    const [searchQuery, setSearchQuery] = useState('');
    const [riskFilter, setRiskFilter] = useState<'all' | 'low' | 'medium' | 'high'>('all');
    const { sortBy, sortOrder, toggleSort, setSortBy, setSortOrder } = useSort<'rank' | 'name' | 'score' | 'drivers'>('rank');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);

    // Track which site rows are expanded to show all issues
    const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());

    useEffect(() => {
        loadLeaderboard();
    }, []);

    const loadLeaderboard = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiClient.getSiteRiskLeaderboard();
            setLeaderboard(response.data);
            // Store breakdown category metadata from backend (single source of truth)
            if (response.breakdownCategories) {
                setBreakdownCategories(response.breakdownCategories);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load sites');
            console.error('Error loading leaderboard:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateSite = async (siteData: CreateSiteRequest) => {
        try {
            setIsCreating(true);
            await apiClient.createSite(siteData);
            toast.success('Site created successfully!');
            setShowAddSite(false);
            await loadLeaderboard();
        } catch (err: any) {
            const errorMessage = err.message || 'Failed to create site';
            toast.error(errorMessage);
            console.error('Error creating site:', err);
        } finally {
            setIsCreating(false);
        }
    };

    const handleOpenEdit = async (siteId: string) => {
        try {
            const response = await apiClient.getSite(siteId);
            setSelectedSite(response.data);
            setShowEditSite(true);
        } catch (err: any) {
            toast.error(err.message || 'Failed to load site details');
        }
    };

    const handleUpdateSite = async (siteId: string, siteData: UpdateSiteRequest) => {
        try {
            setIsUpdating(true);
            await apiClient.updateSite(siteId, siteData);
            toast.success('Site updated successfully!');
            setShowEditSite(false);
            setSelectedSite(null);
            await loadLeaderboard();
        } catch (err: any) {
            toast.error(err.message || 'Failed to update site');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteSite = async (action: 'unassign' | 'reassign', targetSiteId?: string) => {
        if (!siteToDelete) return;
        try {
            setIsDeleting(true);
            await apiClient.deleteSite(siteToDelete.id, { action, targetSiteId });
            toast.success(`${siteToDelete.name} deleted successfully`);
            setSiteToDelete(null);
            await loadLeaderboard();
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete site');
        } finally {
            setIsDeleting(false);
        }
    };

    // Filtered and sorted data
    const filteredData = useMemo(() => {
        let result = [...leaderboard];

        // Apply search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(site =>
                site.siteName.toLowerCase().includes(query) ||
                site.siteCode.toLowerCase().includes(query)
            );
        }

        // Apply risk filter
        if (riskFilter !== 'all') {
            result = result.filter(site => site.riskLevel === riskFilter);
        }

        // Apply sorting
        result.sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case 'rank':
                    comparison = a.rank - b.rank;
                    break;
                case 'name':
                    comparison = a.siteName.localeCompare(b.siteName);
                    break;
                case 'score':
                    comparison = a.totalScore - b.totalScore;
                    break;
                case 'drivers':
                    comparison = a.totalDrivers - b.totalDrivers;
                    break;
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });

        return result;
    }, [leaderboard, searchQuery, riskFilter, sortBy, sortOrder]);

    // Pagination
    const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredData.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredData, currentPage]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, riskFilter, sortBy, sortOrder]);

    // Risk distribution summary (match Widget: include empty sites)
    const sitesWithDrivers = leaderboard.filter(s => s.totalDrivers > 0);
    const riskDistribution = {
        low: sitesWithDrivers.filter(s => s.riskLevel === 'low').length,
        medium: sitesWithDrivers.filter(s => s.riskLevel === 'medium').length,
        high: sitesWithDrivers.filter(s => s.riskLevel === 'high').length,
        noDrivers: leaderboard.filter(s => s.totalDrivers === 0).length,
    };

    // Calculate org-wide average "Best Practice" score
    const orgAverageScore = useMemo(() => {
        if (sitesWithDrivers.length === 0) return 0;
        const totalScore = sitesWithDrivers.reduce((sum, site) => sum + site.totalScore, 0);
        return totalScore / sitesWithDrivers.length;
    }, [sitesWithDrivers]);

    // Thresholds from org scoring config (or defaults: Low < 15, Medium 15-39, High >= 40)
    const getOrgScoreLevel = (score: number): 'low' | 'medium' | 'high' => {
        if (score < lowMediumBoundary) return 'low';
        if (score < mediumHighBoundary) return 'medium';
        return 'high';
    };

    const getRiskColor = (level: string): string => {
        switch (level) {
            case 'low': return '#69BE28';
            case 'medium': return '#f59e0b';
            case 'high': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const getRankBadge = (rank: number) => {
        if (rank === 1) return <span className="text-lg">🥇</span>;
        if (rank === 2) return <span className="text-lg">🥈</span>;
        if (rank === 3) return <span className="text-lg">🥉</span>;
        return <span className="text-sm font-medium text-gray-600">{rank}</span>;
    };

    const exportToCSV = () => {
        const headers = ['Rank', 'Site Name', 'Site Code', 'Risk Score', 'Risk Level', 'Total Drivers', 'Trend', 'Previous Score'];
        const rows = filteredData.map(site => [
            site.rank,
            site.siteName,
            site.siteCode,
            site.totalScore.toFixed(2),
            site.riskLevel,
            site.totalDrivers,
            site.trend,
            site.previousScore?.toFixed(2) ?? 'N/A'
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `site-risk-leaderboard-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-center h-64">
                        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: '#69BE28' }} />
                        <span className="ml-3 text-gray-600 text-lg">Loading site risk data...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-center h-64 text-red-600">
                        <AlertTriangle className="w-6 h-6 mr-2" />
                        <span className="text-lg">{error}</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Sites & Depots</h1>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={loadLeaderboard}
                            disabled={isLoading}
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-md disabled:opacity-50 flex-shrink-0"
                            title="Refresh Sites"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} style={{ color: '#69BE28' }} />
                        </button>
                        <button
                            onClick={exportToCSV}
                            className="p-2 sm:inline-flex sm:items-center sm:px-4 sm:py-2 text-gray-600 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 flex-shrink-0"
                            title="Export to CSV"
                        >
                            <Download className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Export</span>
                        </button>
                        <button
                            onClick={() => setShowAddSite(true)}
                            className="p-2 sm:inline-flex sm:items-center sm:px-4 sm:py-2 text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 flex-shrink-0"
                            style={{ backgroundColor: '#69BE28', '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            title="Add Site"
                        >
                            <Plus className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Add Site</span>
                        </button>
                    </div>
                </div>
                <p className="text-sm sm:text-base text-gray-600">
                    Manage locations and monitor compliance scores
                </p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-5 gap-4 mb-6">
                {/* Company Score - Prominent position */}
                <div
                    className="bg-white rounded-lg shadow p-4 border-l-4 cursor-pointer hover:shadow-md transition-shadow"
                    style={{ borderLeftColor: getRiskColor(getOrgScoreLevel(orgAverageScore)) }}
                    onClick={() => { setRiskFilter('all'); setSortBy('score'); setSortOrder('desc'); }}
                    title="Sort by risk score (highest first)"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Company Score</p>
                            <p className="text-2xl font-bold" style={{ color: getRiskColor(getOrgScoreLevel(orgAverageScore)) }}>
                                {orgAverageScore.toFixed(0)}
                            </p>
                            <p className="text-xs text-gray-400">Average across all sites</p>
                        </div>
                        <Target className="w-8 h-8" style={{ color: getRiskColor(getOrgScoreLevel(orgAverageScore)) }} />
                    </div>
                </div>
                <div
                    className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => { setRiskFilter('all'); setSortBy('rank'); setSortOrder('asc'); }}
                    title="Show all sites"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Sites</p>
                            <p className="text-2xl font-bold text-gray-900">{leaderboard.length}</p>
                        </div>
                        <Building2 className="w-8 h-8 text-gray-400" />
                    </div>
                </div>
                <div
                    className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setRiskFilter('low')}
                    title="Filter by low risk"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Low Risk</p>
                            <p className="text-2xl font-bold text-green-600">{riskDistribution.low}</p>
                        </div>
                        <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                </div>
                <div
                    className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setRiskFilter('medium')}
                    title="Filter by medium risk"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Medium Risk</p>
                            <p className="text-2xl font-bold text-amber-600">{riskDistribution.medium}</p>
                        </div>
                        <AlertTriangle className="w-8 h-8 text-amber-500" />
                    </div>
                </div>
                <div
                    className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setRiskFilter('high')}
                    title="Filter by high risk"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">High Risk</p>
                            <p className="text-2xl font-bold text-red-600">{riskDistribution.high}</p>
                        </div>
                        <ShieldAlert className="w-8 h-8 text-red-500" />
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="flex flex-wrap items-center gap-4">
                    {/* Search */}
                    <div className="flex-1 min-w-64">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search sites..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Risk Filter */}
                    <div className="flex items-center space-x-2">
                        <Filter className="w-4 h-4 text-gray-400" />
                        <select
                            value={riskFilter}
                            onChange={(e) => setRiskFilter(e.target.value as any)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                            <option value="all">All Risk Levels</option>
                            <option value="low">Low Risk Only</option>
                            <option value="medium">Medium Risk Only</option>
                            <option value="high">High Risk Only</option>
                        </select>
                    </div>

                    {/* Results count */}
                    <div className="text-sm text-gray-500">
                        Showing {paginatedData.length} of {filteredData.length} sites
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Table Header */}
                <div className="flex items-center gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-600">
                    <button
                        onClick={() => toggleSort('rank')}
                        className="w-16 flex items-center hover:text-gray-900"
                    >
                        Rank
                        {sortBy === 'rank' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                    </button>
                    <button
                        onClick={() => toggleSort('score')}
                        className="w-16 flex items-center hover:text-gray-900"
                    >
                        Score
                        {sortBy === 'score' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                    </button>
                    <button
                        onClick={() => toggleSort('name')}
                        className="w-40 flex items-center hover:text-gray-900"
                    >
                        Site
                        {sortBy === 'name' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                    </button>
                    <button
                        onClick={() => toggleSort('drivers')}
                        className="w-20 flex items-center hover:text-gray-900"
                    >
                        Drivers
                        {sortBy === 'drivers' && (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />)}
                    </button>
                    <div className="flex-1">Status</div>
                    <div className="w-20 text-right">Actions</div>
                </div>

                {/* Table Body */}
                {paginatedData.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                        <Search className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p>No sites match your search criteria</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-200">
                        {paginatedData.map((site) => {
                            // Build issues array dynamically from backend metadata (single source of truth)
                            const issues: { count: number; label: string; color: string; filter: string; icon: React.ReactNode }[] = [];

                            for (const category of breakdownCategories) {
                                const count = site.breakdown[category.key] || 0;
                                if (count > 0) {
                                    const IconComponent = iconComponents[category.icon] || AlertTriangle;
                                    issues.push({
                                        count,
                                        label: category.label,
                                        color: severityColors[category.severity] || 'gray',
                                        filter: category.filter,
                                        icon: <IconComponent className="w-3 h-3 mr-1" />,
                                    });
                                }
                            }

                            const totalIssues = issues.reduce((sum, i) => sum + i.count, 0);
                            const hasNoIssues = totalIssues === 0;

                            const isExpanded = expandedSites.has(site.siteId);
                            const maxVisible = 5; // Show up to 5 badges before "+X more"
                            const displayIssues = isExpanded ? issues : issues.slice(0, maxVisible);
                            const remainingCount = issues.length - maxVisible;

                            return (
                                <div
                                    key={site.siteId}
                                    className="flex items-center gap-4 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-all"
                                    onClick={(e) => {
                                        // Only navigate if the click wasn't on a button (issue badges or +more)
                                        const target = e.target as HTMLElement;
                                        if (target.closest('button')) return;
                                        navigate(`/drivers?site=${site.siteId}`);
                                    }}
                                >
                                    {/* Rank */}
                                    <div className="w-16 flex items-center justify-center">
                                        {getRankBadge(site.rank)}
                                    </div>

                                    {/* Score */}
                                    <div className="w-16 flex items-center">
                                        <span
                                            className="px-2 py-0.5 rounded text-xs font-bold text-white"
                                            style={{ backgroundColor: getRiskColor(site.riskLevel) }}
                                        >
                                            {site.totalScore.toFixed(0)}
                                        </span>
                                    </div>

                                    {/* Site Name */}
                                    <div className="w-40 min-w-0">
                                        <p className="font-medium text-gray-900 truncate">{site.siteName}</p>
                                    </div>

                                    {/* Drivers */}
                                    <div className="w-20 flex items-center text-gray-600">
                                        <Users className="w-4 h-4 text-gray-400 mr-1.5" />
                                        {site.totalDrivers}
                                    </div>

                                    {/* Status - Inline Badges */}
                                    <div className="flex-1 flex items-center gap-2 flex-wrap min-w-0">
                                        {hasNoIssues ? (
                                            <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                                <CheckCircle className="w-4 h-4 mr-1.5 text-green-500" />
                                                No issues
                                            </div>
                                        ) : (
                                            <>
                                                {displayIssues.map((issue, idx) => (
                                                    <button
                                                        key={idx}
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/drivers?site=${site.siteId}&show=${issue.filter}`); }}
                                                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                                            issue.color === 'red' ? 'bg-red-100 text-red-800 hover:bg-red-200' :
                                                            issue.color === 'yellow' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                                                            'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        }`}
                                                    >
                                                        {issue.icon}
                                                        <span className="font-bold mr-1">{issue.count}</span>
                                                        {issue.label}
                                                    </button>
                                                ))}
                                                {remainingCount > 0 && !isExpanded && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setExpandedSites(prev => new Set(prev).add(site.siteId));
                                                        }}
                                                        className="inline-flex items-center px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
                                                    >
                                                        +{remainingCount} more
                                                    </button>
                                                )}
                                                {isExpanded && issues.length > maxVisible && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setExpandedSites(prev => {
                                                                const next = new Set(prev);
                                                                next.delete(site.siteId);
                                                                return next;
                                                            });
                                                        }}
                                                        className="inline-flex items-center px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
                                                    >
                                                        show less
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {/* Actions */}
                                    <div className="w-20 flex items-center justify-end space-x-1.5 flex-shrink-0">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleOpenEdit(site.siteId); }}
                                            className="text-green-600 hover:text-green-900"
                                            title="Edit site"
                                        >
                                            <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setSiteToDelete({ id: site.siteId, name: site.siteName, totalDrivers: site.totalDrivers }); }}
                                            className="text-red-600 hover:text-red-900"
                                            title="Delete site"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                            Page {currentPage} of {totalPages}
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum: number;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                                            currentPage === pageNum
                                                ? 'text-white'
                                                : 'border border-gray-300 hover:bg-gray-100'
                                        }`}
                                        style={currentPage === pageNum ? { backgroundColor: '#69BE28' } : {}}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Legend */}
            <div className="mt-6 bg-white rounded-lg shadow p-4">
                <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs">
                    <div className="flex items-center">
                        <span className="text-gray-500 font-medium">Score = Average driver risk (lower is better)</span>
                    </div>
                    <div className="h-4 w-px bg-gray-300" />
                    <div className="flex items-center space-x-4">
                        <span className="flex items-center">
                            <span className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: '#69BE28' }} />
                            <span className="text-gray-600">Low &lt;{lowMediumBoundary}</span>
                        </span>
                        <span className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-amber-500 mr-1.5" />
                            <span className="text-gray-600">Medium {lowMediumBoundary}-{mediumHighBoundary - 1}</span>
                        </span>
                        <span className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-red-500 mr-1.5" />
                            <span className="text-gray-600">High {mediumHighBoundary}+</span>
                        </span>
                    </div>
                </div>
            </div>

            {/* Add Site Modal */}
            <AddSiteModal
                isOpen={showAddSite}
                onClose={() => setShowAddSite(false)}
                onSubmit={handleCreateSite}
                isCreating={isCreating}
            />

            {/* Edit Site Modal */}
            <EditSiteModal
                isOpen={showEditSite}
                onClose={() => { setShowEditSite(false); setSelectedSite(null); }}
                onSubmit={handleUpdateSite}
                isUpdating={isUpdating}
                site={selectedSite}
            />

            {/* Delete Site Modal */}
            <DeleteSiteModal
                site={siteToDelete}
                onClose={() => setSiteToDelete(null)}
                onConfirm={handleDeleteSite}
                isDeleting={isDeleting}
            />
        </div>
    );
};

export default SitesPage;
